import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import { Box, Icon, IconWrapper, Input, Link, Optional, Tabs } from '@honda-canada/design-system-react';
import { useTranslation } from 'react-i18next';
import SectionTitleWithTooltip from '../shared/SectionTitleWithTooltip';
import gtmAdditionalAnnualKm from '../../../core/gtm/gtmAdditionalAnnualKm';
import themeStyles from './PaymentAnnualKmAllowance.styles';
import { selectAdditionalLeaseAnnualKmAllowance, selectLeaseAnnualKmAllowance, } from '../../../core/reducers/financialCalculators';
import { useAppSelector } from '../../../core/utils';
import { useFetchCurrentVehicleFinancialCalculatorPayment } from '../../../core/apiHooks/useFetchPayment';
const LEASE_KM_EXCEEDED = 'LeaseKmExceeded';
const Wrapper = themeStyles.apply(Box, 'Wrapper');
const AdditionalKmInput = themeStyles.apply(Input, 'Input');
const AdditionalKmIcon = themeStyles.apply(IconWrapper, 'AdditionalKmIcon');
const AdditionalKmLink = themeStyles.apply(Link, 'AdditionalKmLink');
const WrapperAdditionalKms = themeStyles.apply(Box, 'WrapperAdditionalKms');
const WrapperInputAdditionalKms = themeStyles.apply(Box, 'WrapperInputAdditionalKms');
const PaymentAnnualKmAllowance = (_a) => {
    var _b, _c, _d;
    var { calculatorId, variant = (_b = Tabs === null || Tabs === void 0 ? void 0 : Tabs.variants) === null || _b === void 0 ? void 0 : _b.variantPrimary, disableAllFields, onAnnualKmChanged, onAdditionalKmChanged, } = _a;
    const { t } = useTranslation();
    const additionalLeaseAnnualKmAllowance = useAppSelector(state => selectAdditionalLeaseAnnualKmAllowance(state, calculatorId));
    const [additionalKm, setAdditionalKm] = useState(`${additionalLeaseAnnualKmAllowance}` || '0');
    const leaseAnnualKmAllowance = useAppSelector(state => selectLeaseAnnualKmAllowance(state, calculatorId));
    const { data } = useFetchCurrentVehicleFinancialCalculatorPayment(calculatorId);
    const tabItems = (data === null || data === void 0 ? void 0 : data.leaseKilometers.map(tab => ({
        key: tab.kmAllowance,
        label: tab.kmAllowance,
        content: tab,
    })).sort((a, b) => a.key - b.key)) || [];
    const [showAdditionalKms, setShowAdditionalKms] = useState(!!additionalLeaseAnnualKmAllowance);
    const [selectedTab, setSelectedTab] = useState((_c = tabItems === null || tabItems === void 0 ? void 0 : tabItems.find(tab => tab.key === leaseAnnualKmAllowance)) === null || _c === void 0 ? void 0 : _c.content);
    const hasAdditionalAnnualKm = !!additionalLeaseAnnualKmAllowance;
    const isAdditionalKmsInputVisible = showAdditionalKms || hasAdditionalAnnualKm;
    const hasError = (_d = data === null || data === void 0 ? void 0 : data.ruleExceptions) === null || _d === void 0 ? void 0 : _d.find(exception => exception.type === LEASE_KM_EXCEEDED);
    const clearAdditionalKmsHandler = () => {
        if (onAdditionalKmChanged) {
            onAdditionalKmChanged(0);
        }
        setAdditionalKm('0');
    };
    /**
     * The initial value leaseAnnualKmAllowance comes to us in the payment response.
     * So when we get this value, only then we can set the selected tab.
     * And since we store the value in the state, we must update the state.
     */
    useEffect(() => {
        var _a;
        if (leaseAnnualKmAllowance === 0 && (data === null || data === void 0 ? void 0 : data.leaseAnnualKmAllowance)) {
            setSelectedTab((_a = tabItems === null || tabItems === void 0 ? void 0 : tabItems.find(tab => tab.key === (data === null || data === void 0 ? void 0 : data.leaseAnnualKmAllowance))) === null || _a === void 0 ? void 0 : _a.content);
            if (onAnnualKmChanged) {
                onAnnualKmChanged(data === null || data === void 0 ? void 0 : data.leaseAnnualKmAllowance);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [leaseAnnualKmAllowance, data === null || data === void 0 ? void 0 : data.leaseAnnualKmAllowance, calculatorId]);
    useEffect(() => {
        if (data === null || data === void 0 ? void 0 : data.leaseAdditionalAnnualKm) {
            setAdditionalKm(`${data === null || data === void 0 ? void 0 : data.leaseAdditionalAnnualKm}`);
        }
    }, [hasError, data]);
    const onChangeAnnualKmAllowanceHandler = (index) => {
        var _a, _b, _c;
        const tabLast = (_a = tabItems[tabItems.length - 1]) === null || _a === void 0 ? void 0 : _a.key;
        const tab = (_b = tabItems === null || tabItems === void 0 ? void 0 : tabItems[index]) === null || _b === void 0 ? void 0 : _b.content;
        if (calculatorId && tab) {
            if (tabLast !== tab.key) {
                setShowAdditionalKms(false);
                clearAdditionalKmsHandler();
            }
            setSelectedTab(tab);
            if (onAnnualKmChanged) {
                const kmAllowance = ((_c = tabItems === null || tabItems === void 0 ? void 0 : tabItems[index]) === null || _c === void 0 ? void 0 : _c.label) || 0;
                gtmAdditionalAnnualKm(kmAllowance);
                onAnnualKmChanged(kmAllowance);
            }
        }
    };
    const onChangeAdditionalAdditionalKmsBlurHandler = () => {
        if (onAdditionalKmChanged) {
            onAdditionalKmChanged(parseInt(additionalKm, 10));
        }
        gtmAdditionalAnnualKm(additionalKm);
    };
    const onChangeAdditionalAdditionalKmsHandler = ({ target: { value } }) => {
        let numericValue = value.replace(/[^0-9]/g, '');
        if (numericValue.length > 5) {
            return;
        }
        numericValue = numericValue.replace(/^0+/, '');
        if (numericValue === '') {
            numericValue = '0';
        }
        setAdditionalKm(numericValue);
    };
    const onShowAdditionalAdditionalKmsHandler = () => {
        var _a;
        if (selectedTab) {
            if (onAdditionalKmChanged) {
                onAdditionalKmChanged(0);
            }
            setAdditionalKm('0');
        }
        const tab = (_a = tabItems[tabItems.length - 1]) === null || _a === void 0 ? void 0 : _a.key;
        if (tab) {
            if (onAnnualKmChanged) {
                onAnnualKmChanged(tab);
            }
        }
        setShowAdditionalKms(prev => !prev);
    };
    const renderTab = ({ key, label, index, isActive, ariaLabel }) => (_jsx(Tabs.Tab, { label: label.toLocaleString(), index: index, isActive: isActive, ariaLabel: ariaLabel, variant: variant, isDisabled: disableAllFields }, key));
    if (tabItems.length === 0) {
        return null;
    }
    return (_jsxs(Wrapper, { "data-testid": "cy-payment-calc-annual-allowance", children: [_jsx(SectionTitleWithTooltip, { title: t('Pages.Bap.annualKmLabel'), tooltip: t('Pages.Bap.annualKmTooltipText'), dataTestId: "cy-payment-calc-annual-allowance" }), _jsx(Tabs, { value: tabItems.findIndex(tab => (tab === null || tab === void 0 ? void 0 : tab.key) === leaseAnnualKmAllowance), onChange: onChangeAnnualKmAllowanceHandler, items: tabItems, renderTab: renderTab, ariaLabel: "payment-annual-allowance", variant: variant, isDisabled: disableAllFields }), _jsx(WrapperAdditionalKms, { children: _jsxs(AdditionalKmLink, { href: true, disabled: hasAdditionalAnnualKm, styling: "tertiary2", "data-testid": "cy-payment-calc-annual-allowance-additional-toggle", onClick: () => !hasAdditionalAnnualKm && onShowAdditionalAdditionalKmsHandler(), children: [isAdditionalKmsInputVisible ? t('Pages.Bap.removeAdditionalKmButton') : t('Pages.Bap.addAdditionalKmButton'), _jsx(AdditionalKmIcon, { children: _jsx(Icon, { name: "animatedPlusMinus", iconColor: hasAdditionalAnnualKm ? 'disabled' : 'blue', "data-testid": "cy-payment-calc-annual-allowance-additional-toggle-icon", toggle: isAdditionalKmsInputVisible }) })] }) }), _jsx(Optional, { when: isAdditionalKmsInputVisible, children: _jsxs(WrapperInputAdditionalKms, { children: [_jsx(SectionTitleWithTooltip, { title: t('Pages.Bap.additionalKmLabel'), tooltip: t('Pages.Bap.additionalKmTooltipText'), dataTestId: "cy-payment-calc-annual-allowance-additional" }), _jsx(AdditionalKmInput, { displacedSuffix: t('Pages.Bap.additionalKmPlaceholder', {
                                AdditionalCostPerKm: selectedTab === null || selectedTab === void 0 ? void 0 : selectedTab.additionalCostPerKm,
                            }), value: additionalKm, onChange: onChangeAdditionalAdditionalKmsHandler, onBlur: onChangeAdditionalAdditionalKmsBlurHandler, "data-testid": "cy-payment-calc-annual-allowance-additional-input", disabled: disableAllFields, hasError: !!(hasError === null || hasError === void 0 ? void 0 : hasError.message), errorMessage: t('Pages.Bap.exceedAdditionalKmError') })] }) })] }));
};
export default PaymentAnnualKmAllowance;
